import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Holo3DCard from './m1nt/card/Holo3DCard'
import AuthenticationSeal from './m1nt/card/AuthenticationSeal'
import CardSealBadge from './m1nt/card/CardSealBadge'
import CardAuthLegend from './m1nt/card/CardAuthLegend'
import MediaLightbox from './m1nt/card/MediaLightbox'
import { getChainName } from './m1nt/card/cardFields'
import { FiX, FiExternalLink, FiDownload, FiPackage } from 'react-icons/fi'
import { M1ntButton, M1ntNotice, M1NT_INPUT_CLASS } from './m1nt/ui'
import { getFlowConfig, OWNER_ACTIONS, TIMELINE_STEPS } from '../utils/cardFlowConfig'
import FlowTimeline from './FlowTimeline'
import PaymentModal from './PaymentModal'
import {
  formatCurrency,
  formatDateTime,
  getIPFSUrl,
  getOpenSeaUrl,
  getPolygonscanUrl,
  getPolygonscanTokenUrl,
  isTestnet,
  formatGrade,
} from '../utils/formatters'
import { getOwnerCardDisplayName } from '../utils/ownerCardDisplay'
import {
  confirmOwnerShipment,
  confirmDelivery,
  markNotificationAsRead,
  rejectCustodyTransferRequest,
  approveCustodyTransferRequest,
} from '../services/api'
import { buildTimelineDates } from '../utils/orderTimelineDates'
import { isPortalCustodyTransferRequest } from '../utils/mintLabCustody'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1'
const API_ROOT = API_BASE_URL.replace(/\/api\/v1$/, '')
const CONTRACT_ADDRESS = import.meta.env.VITE_M1NT_CARD_ADDRESS || ''

function formatCustodyCountdown(ms) {
  if (ms <= 0) return '0:00:00'
  const s = Math.floor(ms / 1000)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

const VIEW_CHAIN_ID = parseInt(import.meta.env.VITE_CHAIN_ID || '80002', 10)

/** Small mono caption above every block of the modal. */
const SECTION_LABEL = 'font-m1-mono text-[11px] uppercase tracking-[0.14em] text-m1-muted'
/** Hairline-framed block (instructions, descriptions). */
const BLOCK = 'border border-m1-line bg-white/[0.02] p-4'
/** Quiet text link inside the modal. */
const TEXT_LINK =
  'inline-flex items-center gap-1.5 text-[14px] text-m1-ink underline decoration-white/30 underline-offset-4 transition-colors hover:decoration-white'

export default function CardFlowModal({
  card,
  order,
  imageUrl: imageUrlProp,
  notifications = [],
  wallet,
  onClose,
  onActionComplete,
  custodyRequest = null,
}) {
  const navigate = useNavigate()
  const [cardViewerOpen, setCardViewerOpen] = useState(false)
  const [showPayModal, setShowPayModal] = useState(false)
  const [showGradeDetails, setShowGradeDetails] = useState(false)
  const [confirmShipmentOpen, setConfirmShipmentOpen] = useState(false)
  const [trackingNumber, setTrackingNumber] = useState('')
  const [submittingShipment, setSubmittingShipment] = useState(false)
  const [submittingDelivery, setSubmittingDelivery] = useState(false)
  const [submittingCustody, setSubmittingCustody] = useState(false)
  const [actionFeedback, setActionFeedback] = useState({ type: null, message: '' })
  const [custodyApproveWarningOpen, setCustodyApproveWarningOpen] = useState(false)
  const [custodyNow, setCustodyNow] = useState(() => Date.now())
  const sectionRefs = useRef({})
  const scrollContainerRef = useRef(null)

  useEffect(() => {
    setCustodyApproveWarningOpen(false)
  }, [custodyRequest?.id])

  useEffect(() => {
    if (!custodyRequest?.dueAt || custodyRequest.isExpired) return undefined
    const t = setInterval(() => setCustodyNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [custodyRequest?.id, custodyRequest?.dueAt, custodyRequest?.isExpired])

  const custodyLeftMs = custodyRequest?.dueAt
    ? new Date(custodyRequest.dueAt).getTime() - custodyNow
    : 0
  const custodyWindowExpired = Boolean(
    custodyRequest && (custodyRequest.isExpired || custodyLeftMs <= 0)
  )
  const portalCustodyTransfer = isPortalCustodyTransferRequest(custodyRequest)
  const portalRecipientLabel =
    custodyRequest?.beneficiary?.name?.trim() ||
    custodyRequest?.beneficiary?.email?.trim() ||
    'the selected user'

  // Mark notifications as read when modal opens
  useEffect(() => {
    if (!wallet || !order?.id) return
    notifications
      .filter((n) => !n.read && (n.orderId === order.id || n.cardId === card?.id))
      .forEach((n) => markNotificationAsRead(n.id, wallet).catch(() => {}))
  }, [wallet, order?.id, card?.id])

  // Reset grade details visibility when changing order/modal content
  useEffect(() => {
    setShowGradeDetails(false)
  }, [order?.id])

  const status = order?.status || 'PENDING_QUOTE'
  const config = getFlowConfig(status)
  const actionConfig = config.ownerAction ? OWNER_ACTIONS[config.ownerAction] : null
  const timelineDates = buildTimelineDates(order)

  const imageUrl =
    (imageUrlProp && !String(imageUrlProp).startsWith('gs://') ? imageUrlProp : null) ||
    (card?.frontImage && !String(card.frontImage).startsWith('gs://') ? card.frontImage : null) ||
    getIPFSUrl(card?.metadata?.images?.frontHash) ||
    '/placeholder-card.png'

  const cardDisplayName = getOwnerCardDisplayName(card)

  const handlePayClick = () => setShowPayModal(true)
  const handlePaymentSuccess = () => {
    setShowPayModal(false)
    onActionComplete?.()
  }

  const handleConfirmShipmentSubmit = async () => {
    if (!order?.id || !trackingNumber.trim()) return
    setActionFeedback({ type: null, message: '' })
    setSubmittingShipment(true)
    try {
      await confirmOwnerShipment(order.id, {
        trackingNumber: trackingNumber.trim(),
      })
      setConfirmShipmentOpen(false)
      setTrackingNumber('')
      onActionComplete?.()
    } catch (err) {
      console.error(err)
      setActionFeedback({
        type: 'error',
        message: err.response?.data?.error || 'Failed to confirm shipment.',
      })
    } finally {
      setSubmittingShipment(false)
    }
  }

  const handleConfirmDeliveryClick = async () => {
    // Works with wallet auth or email session token (interceptor).
    if (!order?.id) return
    setActionFeedback({ type: null, message: '' })
    setSubmittingDelivery(true)
    try {
      await confirmDelivery(order.id, wallet)
      setActionFeedback({ type: 'success', message: 'Receipt confirmed successfully.' })
      onActionComplete?.()
    } catch (err) {
      console.error(err)
      setActionFeedback({
        type: 'error',
        message: err.response?.data?.error || 'Failed to confirm delivery.',
      })
    } finally {
      setSubmittingDelivery(false)
    }
  }

  const handleRejectCustody = async (opts = {}) => {
    const { skipConfirm = false } = opts
    if (!wallet || !custodyRequest?.id) return
    if (
      !skipConfirm &&
      !window.confirm('Reject this custody transfer request? The Lab will be notified.')
    ) {
      return
    }
    setSubmittingCustody(true)
    try {
      await rejectCustodyTransferRequest(custodyRequest.id, wallet)
      onActionComplete?.()
      onClose?.()
    } catch (err) {
      console.error(err)
      alert(err.response?.data?.error || 'Failed to reject the request.')
    } finally {
      setSubmittingCustody(false)
    }
  }

  const handleApproveCustodyAfterWarning = async () => {
    if (!wallet || !custodyRequest?.id) return
    setCustodyApproveWarningOpen(false)
    setSubmittingCustody(true)
    try {
      await approveCustodyTransferRequest(custodyRequest.id, wallet)
      onActionComplete?.()
      onClose?.()
    } catch (err) {
      console.error(err)
      alert(err.response?.data?.error || 'Failed to approve the request.')
    } finally {
      setSubmittingCustody(false)
    }
  }

  const openCustodyApproveWarning = () => {
    if (custodyWindowExpired) return
    setCustodyApproveWarningOpen(true)
  }

  const handleDoNotAuthorizeFromWarning = async () => {
    setCustodyApproveWarningOpen(false)
    await handleRejectCustody({ skipConfirm: true })
  }

  const scrollToSection = (key) => {
    const el = sectionRefs.current[key]
    const scroller = scrollContainerRef.current
    if (!el || !scroller) return
    const scrollerRect = scroller.getBoundingClientRect()
    const elRect = el.getBoundingClientRect()
    const nextTop = scroller.scrollTop + (elRect.top - scrollerRect.top) - 8
    scroller.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' })
  }

  const tokenId = order?.tokenId ?? card?.tokenId
  const openSeaUrl = CONTRACT_ADDRESS && tokenId ? getOpenSeaUrl(CONTRACT_ADDRESS, tokenId) : null
  const polygonScanTx = order?.nftTxHash ? getPolygonscanUrl(order.nftTxHash) : null
  const polygonScanToken =
    CONTRACT_ADDRESS && tokenId ? getPolygonscanTokenUrl(CONTRACT_ADDRESS, tokenId) : null

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4 animate-modal-backdrop"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="card-flow-modal-title"
      >
        <div
          className="card-flow-modal m1nt-plate w-full max-w-2xl max-h-[90dvh] sm:max-h-[85dvh] min-h-0 flex flex-col rounded-t-[16px] sm:rounded-[16px] animate-modal-slide"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-shrink-0 items-center justify-between border-b border-m1-line px-5 py-3">
            <h2
              id="card-flow-modal-title"
              className="font-m1-display text-[24px] font-normal tracking-[-0.01em] text-m1-ink"
            >
              Card Detail
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center text-m1-muted transition-colors hover:text-m1-ink"
              aria-label="Close"
            >
              <FiX className="h-5 w-5" />
            </button>
          </div>

          {/* Card summary: fixed while body scrolls */}
          <div className="flex-shrink-0 border-b border-m1-line px-5 pb-4 pt-4">
            <div className="flex gap-4">
              <div className="w-[116px] flex-shrink-0 sm:w-[140px]">
                <Holo3DCard
                  imageUrl={imageUrl}
                  fallbackImageUrl="/placeholder-card.png"
                  alt={cardDisplayName}
                  showAfterSplash
                  onActivate={() => setCardViewerOpen(true)}
                />
              </div>
              <div className="flex-1 min-w-0">
                {/* Card name uses the sans font again (client request).
                    To restore Bodoni, use: break-words font-m1-display text-[24px] font-normal leading-tight tracking-[-0.01em] text-m1-ink */}
                <h3 className="break-words font-sans text-xl font-semibold leading-tight tracking-tight text-m1-ink">
                  {cardDisplayName}
                </h3>
                <p className="mt-1 text-[15px] font-light text-m1-muted">{card?.cardType || '—'}</p>
                <p className="mt-2 inline-flex items-center gap-2 font-m1-mono text-[11px] uppercase tracking-[0.12em] text-m1-ink">
                  <span className="h-1.5 w-1.5 rounded-full bg-white" aria-hidden="true" />
                  {config.label}
                </p>
                {order?.id && (
                  <p className="mt-3 break-all font-m1-mono text-[11px] text-m1-muted">
                    Order ID: {order.orderId || order.id}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div
            ref={scrollContainerRef}
            className="card-flow-modal-body min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain px-5 py-5 space-y-6"
          >
            {tokenId != null && (
              <AuthenticationSeal
                tokenId={tokenId}
                chainId={VIEW_CHAIN_ID}
                chainName={getChainName(VIEW_CHAIN_ID)}
                onActivate={card?.id ? () => navigate(`/card/${card.id}`) : undefined}
              />
            )}

            {custodyRequest && (
              <div className="space-y-3 border border-m1-amber/40 bg-m1-amber/[0.06] p-4">
                <h4 className={`${SECTION_LABEL} text-m1-amber`}>Custody transfer</h4>
                {portalCustodyTransfer ? (
                  <>
                    <p className="text-[15px] font-light leading-relaxed text-m1-ink-soft">
                      The Lab asked to change who owns this card in your M1NT account. This updates
                      the listed owner in the app only — no wallet transaction is required. The NFT
                      stays in Mint Lab on-chain custody until the new owner connects a wallet. You
                      have 24 hours to respond.
                    </p>
                    <p className="text-[15px] font-normal text-m1-ink">New owner in M1NT</p>
                    <p className="text-[15px] text-m1-ink">{portalRecipientLabel}</p>
                    {custodyRequest.beneficiary?.email ? (
                      <p className="text-[13px] text-m1-muted">
                        {custodyRequest.beneficiary.email}
                      </p>
                    ) : null}
                  </>
                ) : (
                  <>
                    <p className="text-[15px] font-light leading-relaxed text-m1-ink-soft">
                      The Lab asked to move on-chain ownership to a new wallet. We will request your
                      approval before any transfer. You have 24 hours to respond. Proposed new
                      owner:
                    </p>
                    <p
                      className="mt-1 break-all font-m1-mono text-[12px] text-m1-ink"
                      title={custodyRequest.newOwnerWallet}
                    >
                      {custodyRequest.newOwnerWallet}
                    </p>
                  </>
                )}
                {!custodyWindowExpired && custodyRequest.dueAt && (
                  <p className="font-m1-mono text-[12px] text-m1-amber">
                    Time left: {formatCustodyCountdown(custodyLeftMs)}
                  </p>
                )}
                {custodyWindowExpired && (
                  <p className="text-[15px] text-red-300/95">
                    The 24-hour response window has ended. This request is no longer valid. Close
                    this dialog and refresh; the Lab may send a new request.
                  </p>
                )}
                {!custodyWindowExpired && (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <M1ntButton
                      solid
                      onClick={openCustodyApproveWarning}
                      disabled={submittingCustody}
                      className="flex-1"
                    >
                      {submittingCustody
                        ? '…'
                        : portalCustodyTransfer
                          ? 'Confirm transfer'
                          : 'Approve transfer'}
                    </M1ntButton>
                    <M1ntButton
                      onClick={handleRejectCustody}
                      disabled={submittingCustody}
                      className="flex-1"
                    >
                      Reject
                    </M1ntButton>
                  </div>
                )}
              </div>
            )}

            {/* Timeline */}
            <div ref={(el) => (sectionRefs.current.timeline = el)}>
              <h4 className={`${SECTION_LABEL} mb-5`}>Progress</h4>
              <FlowTimeline
                currentStep={config.step}
                dates={timelineDates}
                steps={TIMELINE_STEPS}
              />
            </div>

            {/* Current step description */}
            <div className={BLOCK}>
              {(status === 'NFT_MINTED' || status === 'MINTED') && (
                <>
                  <p className="text-[15px] font-normal text-m1-ink">
                    Your card has already been minted.
                  </p>
                  {order?.mintedAt && (
                    <p className="mt-1 font-m1-mono text-[11px] text-m1-muted">
                      Minted on {formatDateTime(order.mintedAt)}
                    </p>
                  )}
                  <p className="mt-2 text-[15px] font-light leading-relaxed text-m1-ink-soft">
                    {config.description}
                  </p>
                </>
              )}
              {status !== 'NFT_MINTED' && status !== 'MINTED' && (
                <p className="text-[15px] font-light leading-relaxed text-m1-ink-soft">
                  {config.description}
                </p>
              )}
            </div>

            {/* Quote section */}
            {(order?.priceUSD != null || order?.price != null) && (
              <div
                ref={(el) => (sectionRefs.current.quote = el)}
                className="space-y-2 border-t border-m1-line pt-5"
              >
                <h4 className={SECTION_LABEL}>Quote</h4>
                <p className="text-[28px] font-light tabular-nums text-m1-ink">
                  {order.priceUSD != null
                    ? `$${parseFloat(order.priceUSD).toFixed(2)} USD`
                    : `${formatCurrency(order.price)} ETH`}
                </p>
              </div>
            )}

            {/* Payment section */}
            <div
              ref={(el) => (sectionRefs.current.payment = el)}
              className="border-t border-m1-line pt-5"
            >
              <h4 className={`${SECTION_LABEL} mb-3`}>Payment</h4>
              {order?.paid ? (
                <div className="space-y-2 text-[15px] text-m1-ink-soft">
                  <p className="flex items-center gap-2">
                    <i className="ri-check-line text-lg text-m1-ice" aria-hidden="true" />
                    Paid at {formatDateTime(order.paidAt)}
                  </p>
                  {order.payment?.txHash && (
                    <a
                      href={getPolygonscanUrl(order.payment.txHash)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={TEXT_LINK}
                    >
                      View transaction <FiExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              ) : ['QUOTE_SENT', 'AWAITING_PAYMENT', 'PRICE_SET', 'PAYMENT_PENDING'].includes(
                  status
                ) && config.ownerAction === 'PAY' ? (
                <M1ntButton solid onClick={handlePayClick} className="min-h-[48px] w-full">
                  Pay now —{' '}
                  {order.priceUSD != null
                    ? `$${parseFloat(order.priceUSD).toFixed(2)}`
                    : `${formatCurrency(order.price)} ETH`}
                </M1ntButton>
              ) : (
                <p className="text-[15px] font-light text-m1-muted">
                  Payment becomes available after the lab sends your quote and you accept it.
                </p>
              )}
            </div>

            {/* Shipping section */}
            {['AWAITING_SHIPMENT', 'SHIPPED_BY_OWNER', 'CARD_RECEIVED'].includes(status) && (
              <div
                ref={(el) => (sectionRefs.current.shipping = el)}
                className="space-y-3 border-t border-m1-line pt-5"
              >
                <h4 className={SECTION_LABEL}>Shipping</h4>
                {order?.shippingInstructions && (
                  <div className={`${BLOCK} text-[15px] font-light text-m1-ink-soft`}>
                    <p className="mb-1 font-normal text-m1-ink">Instructions</p>
                    <p className="whitespace-pre-wrap">{order.shippingInstructions}</p>
                  </div>
                )}
                {order?.shippingLabelUrl && (
                  <a
                    href={`${API_ROOT}${order.shippingLabelUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={TEXT_LINK}
                  >
                    <FiDownload className="h-4 w-4" /> Download label
                  </a>
                )}
                {status === 'AWAITING_SHIPMENT' && !confirmShipmentOpen && (
                  <M1ntButton
                    solid
                    onClick={() => setConfirmShipmentOpen(true)}
                    className="min-h-[48px] w-full"
                  >
                    Confirm shipment
                  </M1ntButton>
                )}
                {status === 'AWAITING_SHIPMENT' && confirmShipmentOpen && (
                  <div className={`${BLOCK} space-y-3`}>
                    <label className="block text-[14px] text-m1-muted">Tracking number *</label>
                    <input
                      type="text"
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      placeholder="Enter tracking number"
                      className={M1NT_INPUT_CLASS}
                    />
                    <div className="flex gap-2">
                      <M1ntButton
                        solid
                        onClick={handleConfirmShipmentSubmit}
                        disabled={submittingShipment || !trackingNumber.trim()}
                        className="flex-1"
                      >
                        {submittingShipment ? 'Confirming…' : 'Submit'}
                      </M1ntButton>
                      <M1ntButton onClick={() => setConfirmShipmentOpen(false)}>Cancel</M1ntButton>
                    </div>
                  </div>
                )}
                {status === 'SHIPPED_BY_OWNER' && order?.ownerTrackingNumber && (
                  <p className="text-[15px] text-m1-ink-soft">
                    Tracking:{' '}
                    <span className="font-m1-mono text-m1-ink">{order.ownerTrackingNumber}</span>
                  </p>
                )}
              </div>
            )}

            {/* Grading section */}
            {(order?.grade != null || order?.gradeOverall != null) && showGradeDetails && (
              <div
                ref={(el) => (sectionRefs.current.graded = el)}
                className="space-y-3 border-t border-m1-line pt-5"
              >
                <h4 className={SECTION_LABEL}>Grade</h4>
                <div className="grid grid-cols-2 gap-px border border-m1-line bg-m1-line sm:grid-cols-5">
                  <div className="col-span-2 bg-m1-ground p-4 sm:col-span-1">
                    <p className={SECTION_LABEL}>Overall</p>
                    <p className="mt-3 text-[36px] font-normal leading-none tabular-nums text-m1-ink">
                      {formatGrade(order.gradeOverall ?? order.grade)}
                    </p>
                  </div>
                  {[
                    ['Centering', order.gradeCentering],
                    ['Corners', order.gradeCorners],
                    ['Edges', order.gradeEdges],
                    ['Surface', order.gradeSurface],
                  ]
                    .filter(([, value]) => value != null)
                    .map(([label, value]) => (
                      <div key={label} className="bg-m1-ground p-4">
                        <p className={SECTION_LABEL}>{label}</p>
                        <p className="mt-3 text-[24px] font-normal leading-none tabular-nums text-m1-ink">
                          {value}
                        </p>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* NFT section */}
            {(tokenId != null || order?.nftTxHash) && (
              <div
                ref={(el) => (sectionRefs.current.nft = el)}
                className="space-y-3 border-t border-m1-line pt-5"
              >
                <h4 className={SECTION_LABEL}>NFT</h4>
                <p className="font-m1-mono text-[15px] text-m1-ink">Token ID: #{tokenId}</p>
                {CONTRACT_ADDRESS && (
                  <p className="break-all font-m1-mono text-[12px] text-m1-muted">
                    Contract: {CONTRACT_ADDRESS}
                  </p>
                )}
                <div className="flex flex-wrap gap-3">
                  {polygonScanTx && (
                    <M1ntButton
                      as="a"
                      href={polygonScanTx}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="min-h-[40px] px-4"
                    >
                      <FiExternalLink className="h-4 w-4" /> Explorer
                    </M1ntButton>
                  )}
                  {openSeaUrl && (
                    <M1ntButton
                      as="a"
                      href={openSeaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="min-h-[40px] px-4"
                    >
                      <FiExternalLink className="h-4 w-4" /> OpenSea
                      {isTestnet() ? ' (Testnet)' : ''}
                    </M1ntButton>
                  )}
                </div>
              </div>
            )}

            {/* Return shipping + Confirm delivery */}
            {status === 'SHIPPED_TO_OWNER' && (
              <div
                ref={(el) => (sectionRefs.current.returned = el)}
                className="space-y-3 border-t border-m1-line pt-5"
              >
                <h4 className={SECTION_LABEL}>Return shipment</h4>
                {order?.returnTrackingNumber && (
                  <p className="text-[15px] text-m1-ink-soft">
                    Tracking:{' '}
                    <span className="font-m1-mono text-m1-ink">{order.returnTrackingNumber}</span>
                  </p>
                )}
                <M1ntButton
                  solid
                  onClick={handleConfirmDeliveryClick}
                  disabled={submittingDelivery}
                  className="min-h-[48px] w-full"
                >
                  {submittingDelivery ? 'Confirming…' : 'Confirm receipt'}
                </M1ntButton>
              </div>
            )}

            {actionFeedback.message && (
              <M1ntNotice
                tone={actionFeedback.type === 'success' ? 'violet' : 'amber'}
                role={actionFeedback.type === 'success' ? 'status' : 'alert'}
                className="!rounded-none !p-3 !text-[14px]"
              >
                {actionFeedback.message}
              </M1ntNotice>
            )}

            {/* Generic action button (VIEW_GRADE / VIEW_NFT scroll) */}
            {actionConfig &&
              !['PAY', 'CONFIRM_SHIPMENT', 'CONFIRM_DELIVERY'].includes(config.ownerAction) &&
              !(config.ownerAction === 'VIEW_GRADE' && showGradeDetails) && (
                <M1ntButton
                  solid
                  onClick={() => {
                    if (config.ownerAction === 'VIEW_GRADE') {
                      setShowGradeDetails(true)
                      setTimeout(() => scrollToSection('graded'), 0)
                    }
                    if (config.ownerAction === 'VIEW_NFT') scrollToSection('nft')
                  }}
                  className="min-h-[48px] w-full"
                >
                  {actionConfig.label}
                  <i className="ri-arrow-down-line text-lg" aria-hidden="true" />
                </M1ntButton>
              )}
          </div>
        </div>
      </div>

      {custodyApproveWarningOpen && custodyRequest && !custodyWindowExpired && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => !submittingCustody && setCustodyApproveWarningOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="custody-approve-warning-title"
        >
          <div
            className="m1nt-plate w-full max-w-md rounded-[16px] p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              id="custody-approve-warning-title"
              className="mb-3 font-m1-display text-[24px] font-normal tracking-[-0.01em] text-m1-ink"
            >
              {portalCustodyTransfer
                ? 'Confirm ownership change in M1NT?'
                : 'Approve custody transfer?'}
            </h3>
            <p className="text-[15px] font-light leading-relaxed text-m1-ink-soft mb-2">
              {portalCustodyTransfer ? (
                <>
                  This only changes who is listed as the owner of this card inside M1NT. No
                  blockchain transaction will be sent from your wallet. The NFT will remain in Mint
                  Lab on-chain custody until{' '}
                  <strong className="font-normal text-m1-ink">{portalRecipientLabel}</strong>{' '}
                  connects a wallet and requests delivery.
                </>
              ) : (
                <>This action may be irreversible once the Lab completes the on-chain transfer.</>
              )}{' '}
              Please decide before the timer runs out. If you do not approve in time, this request
              will be automatically cancelled.
            </p>
            {custodyRequest.dueAt && (
              <p className="mb-4 font-m1-mono text-[13px] text-m1-amber">
                Time left: {formatCustodyCountdown(custodyLeftMs)}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <M1ntButton
                onClick={handleDoNotAuthorizeFromWarning}
                disabled={submittingCustody}
                className="flex-1"
              >
                Do not authorize
              </M1ntButton>
              <M1ntButton
                solid
                onClick={handleApproveCustodyAfterWarning}
                disabled={submittingCustody}
                className="flex-1"
              >
                {submittingCustody
                  ? '…'
                  : portalCustodyTransfer
                    ? 'Yes, change owner'
                    : 'I understand, approve'}
              </M1ntButton>
            </div>
          </div>
        </div>
      )}

      <PaymentModal
        order={order}
        isOpen={showPayModal}
        onClose={() => setShowPayModal(false)}
        onSuccess={handlePaymentSuccess}
      />
      <MediaLightbox
        media={cardViewerOpen ? { kind: 'image', url: imageUrl, alt: cardDisplayName } : null}
        onClose={() => setCardViewerOpen(false)}
        badge={tokenId != null ? <CardSealBadge /> : undefined}
        legend={tokenId != null ? <CardAuthLegend /> : undefined}
        immersive
        tilt
      />
    </>
  )
}
